import React from "react";
import { Container } from "react-bootstrap";
import Particle from "../Particle";
import { motion } from "framer-motion";
import "../../style.css";
import { FaCheckCircle } from "react-icons/fa";

const experiences = [
  {
    title: "Cloud & DevOps Engineer",
    subtitle: "Unloq® (UK) | January 2025 – Present",
    bullets: [
      "Building and managing AI infrastructure for UK's most advanced strategic intelligence engine, supporting decision intelligence platforms used by enterprise clients",
      
      "Implementing Infrastructure as Code using Terraform and AWS services (EC2, S3, RDS, Lambda, VPC, IAM) to provision and manage cloud resources with 99.9% uptime",
      
      "Developing containerized applications using Docker and Kubernetes, managing multi-container deployments with automated scaling and health monitoring",
      
      "Creating CI/CD pipelines with Jenkins and GitHub Actions for automated testing, building, and deployment of microservices, reducing deployment time by 70%",
      
      "Collaborating with CTO Satyen Fakey and CEO Yiannis Maos MBE on innovative cloud solutions, contributing to architectural decisions for modern platform builds",
      
      "Writing Python and Bash automation scripts for cloud operations, infrastructure monitoring, and DevOps workflows, processing 100,000+ daily operations"
    ]
  },
  {
    title: "Research & Development Intern",
    subtitle: "ISRO Ahmedabad | Oct 2023 – Dec 2023",
    bullets: [
      "Migrated satellite monitoring system from Arduino to Raspberry Pi using Python, improving mission efficiency by 30% through automated data processing and real-time diagnostics",
      
      "Developed Python automation scripts for Linux-based satellite telemetry systems, processing 10,000+ data points daily with 99.8% accuracy",
      
      "Collaborated with senior scientists to debug satellite communication protocols, reducing data loss incidents by 40% through systematic troubleshooting",
      
      "Gained hands-on experience with embedded Linux, bash scripting, and automated monitoring—skills directly applicable to cloud infrastructure management and DevOps practices"
    ]
  },
  {
    title: "Full-Stack Developer Intern",
    subtitle: "Tech-Octa-Net Services | Aug 2023 – Oct 2023",
    bullets: [
      "Optimized frontend code (HTML/CSS/JavaScript), reducing page load time by 45% and improving Google Lighthouse performance score from 62 to 89",
      
      "Built REST APIs using Java Servlets and JDBC, handling 500+ daily requests with MySQL database integration and comprehensive error handling",
      
      "Implemented user authentication system using session management and JDBC, securing 200+ user accounts with encrypted password storage and role-based access control",
      
      "Debugged production issues through systematic log analysis and testing, improving application stability by 35% and reducing bug resolution time from 3 days to 8 hours"
    ]
  },
  {
    title: "Teaching Assistant - Java & Data Structures",
    subtitle: "Coding Ninjas | Nov 2022 – Jan 2023",
    bullets: [
      "Mentored 60+ students in Java programming and DSA fundamentals, maintaining 4.1/5 rating through clear explanations and personalized guidance",
      
      "Resolved 300+ technical doubts covering topics from basic arrays to advanced dynamic programming, debugging complex code issues and explaining time/space complexity optimization",
      
      "Reviewed and debugged 100+ student assignments, identifying common coding pitfalls and teaching best practices for writing clean, efficient, maintainable code",
      
      "Developed strong technical communication and problem-solving skills essential for DevOps collaboration, documentation, and cross-team knowledge sharing"
    ]
  }
];

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { type: "spring", duration: 0.7 } }
};

function Experience() {
  return (
    <Container fluid className="experience-section timeline-bg" id="experience" style={{ minHeight: "100vh", padding: "80px 0" }}>
      <Particle />
      <Container>
        <h1 className="section-heading" style={{ color: "white" }}>
          My <span className="purple">Work Experience</span>
        </h1>
        <div className="vertical-experience-list">
          {experiences.map((exp, idx) => (
            <motion.div
              className={
                "vertical-experience-card" +
                (exp.title.includes("Unloq") || exp.subtitle.includes("Unloq") ? " unloq-highlight" : "") +
                (exp.title.includes("Octa-Net") || exp.title.includes("OctaNet") ? " octanet" : "")
              }
              key={idx}
              initial="hidden"
              animate="visible"
              variants={cardVariants}
              whileHover={{ scale: 1.03, boxShadow: "0px 8px 32px 0px rgba(80, 0, 200, 0.18)" }}
            >
              <div className="timeline-title">{exp.title}</div>
              <div className="timeline-subtitle">{exp.subtitle}</div>
              <div className="experience-bullets-horizontal">
                {exp.bullets.map((b, i) => (
                  <span className="exp-bullet-item" key={i}>
                    <FaCheckCircle className="exp-bullet-icon" />
                    <span>{b}</span>
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </Container>
    </Container>
  );
}

export default Experience;

/* Add the following CSS to style.css or in a <style> block:

.vertical-experience-list {
  display: flex;
  flex-direction: column;
  gap: 36px;
  margin-top: 60px;
}
.vertical-experience-card {
  background: rgba(20, 15, 35, 0.95);
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(80,0,200,0.10);
  padding: 28px 32px;
  color: #fff;
  border-left: 4px solid #c770f0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  transition: all 0.3s ease;
}
.vertical-experience-card.unloq-highlight {
  border-left: 4px solid #00d4ff;
  background: rgba(0, 212, 255, 0.05);
}
.experience-bullets-horizontal {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 18px;
  margin-top: 18px;
}
.exp-bullet-item {
  display: flex;
  align-items: flex-start;
  background: rgba(199,112,240,0.08);
  border-radius: 8px;
  padding: 10px 16px;
  color: #e0d6f7;
  font-size: 1em;
  min-width: 180px;
  max-width: 320px;
  box-shadow: 0 2px 8px rgba(80,0,200,0.06);
  gap: 8px;
}
.unloq-highlight .exp-bullet-item {
  background: rgba(0, 212, 255, 0.08);
}
.exp-bullet-icon {
  color: #c770f0;
  margin-right: 8px;
  font-size: 1.1em;
  margin-top: 2px;
}
.unloq-highlight .exp-bullet-icon {
  color: #00d4ff;
}
@media (max-width: 900px) {
  .vertical-experience-card { padding: 18px 10px; }
  .exp-bullet-item { min-width: 120px; max-width: 100%; font-size: 0.98em; }
  .experience-bullets-horizontal { gap: 10px; }
}
*/
